import { applyFolderAsOperationFr } from "@Front/Utils/applyFolderAsOperationFr";
import { normalizeSiteTextLocale } from "@Front/Stores/siteTextLocale";
import { SITE_TEXT_FALLBACKS_FR_TAIL } from "./defaultSiteTextFallbacksFrTail";
import type { SiteTextFallbackDef, SiteTextFallbackMap } from "./defaultSiteTextFallbacksTypes";

export type { SiteTextFallbackDef, SiteTextFallbackMap } from "./defaultSiteTextFallbacksTypes";

function loginMissingProfessionalEmailBodyFallback(): string {
	return "La connexion des professionnels (Id.not) nécessite une adresse e-mail d’entreprise reconnue.";
}

/**
 * Clés référencées par le front avant publication en base ou si le seed
 * d’environnement n’a pas encore été aligné. Les entrées renvoyées par l’API
 * restent prioritaires (`persistEntries` puis `applyFallbackEntriesForLocale`).
 * `SiteTextsStore.get` et `SiteTextsContext` (preload) utilisent `getSiteTextFallbackDef`
 * pour éviter le log « Missing site text » lorsque la clé n’est pas encore en map.
 */
const SITE_TEXT_FALLBACKS_FR_BASE: SiteTextFallbackMap = {
	"branding.productName": ["branding", "LeCoffre.io"],
	"branding.metaDescription": ["branding", "Espace documentaire sécurisé pour les offices notariaux, leurs clients et leurs tiers."],
	"components.chipContainer.searchPlaceholder": ["components", "Rechercher"],
	"components.dropdown.defaultAriaLabel": ["components", "Dropdown"],
	"documentTypes.createTitle": ["documentTypes", "Créer un type d'acte"],
	"documentTypes.editTitle": ["documentTypes", "Modifier un type de document"],
	"documentTypes.listBackMobile": ["documentTypes", "Liste des types d'actes"],
	"documentTypes.listTitle": ["documentTypes", "Liste des documents"],
	"documentTypes.namePlaceholder": ["documentTypes", "Nom du document"],
	"documentTypes.privateDescriptionPlaceholder": ["documentTypes", "Description visible par les collaborateurs de l'office"],
	"documentTypes.publicDescriptionPlaceholder": ["documentTypes", "Description visible par les clients de l'office"],
	"folder.participant.editInfoCta": ["folder", "Modifier les informations"],
	"folder.participant.lastLoginLabel": ["folder", "Dernière connexion"],
	"folder.participant.status.emailVerified": ["folder", "Email vérifié"],
	"folder.participant.status.verificationPending": ["folder", "En attente de vérification"],
	"folder.participant.status.revoked": ["folder", "Accès révoqué"],
	"folder.participant.status.invited": ["folder", "Invité"],
	"folder.participant.status.active": ["folder", "Actif"],
	"folder.participant.status.inactive": ["folder", "Inactif"],
	"folder.participant.status.pending": ["folder", "En attente"],
	"folder.participant.status.error": ["folder", "En erreur"],
	"folder.participant.status.unknown": ["folder", "Statut inconnu"],
	"folder.participant.accessInfoAriaLabel": ["folder", "Informations d’accès du membre"],
	"folder.participant.accessInfoModal.title": ["folder", "Informations d’accès"],
	"folder.participant.accessInfoModal.intro": [
		"folder",
		"Rappel pour le membre (lecture seule). Ces éléments facilitent l’assistance et la vérification des accès.",
	],
	"folder.participant.accessInfoModal.closeCta": ["folder", "Fermer"],
	"folder.participant.accessInfoModal.generateCodeCta": ["folder", "Générer et afficher un code d'accès"],
	"folder.participant.accessInfoModal.generateCodeEmailSentNoCode": [
		"folder",
		"L’e-mail avec le code a été envoyé. Dans ce cas, le code ne peut pas être affiché ici (envoi déjà en attente). Vérifiez la boîte du tiers ou réessayez dans quelques instants.",
	],
	"folder.participant.accessInfoModal.generateCodeFailed": [
		"folder",
		"La génération ou l’envoi du code n’a pas abouti. Vérifiez les coordonnées du membre ou réessayez plus tard.",
	],
	"folder.participant.accessInfo.field.uuid": ["folder", "Identifiant unique (UUID)"],
	"folder.participant.accessInfo.field.idNotMember": ["folder", "Identifiant IdNot du membre"],
	"folder.participant.accessInfo.field.lecoffreUserActive": ["folder", "Utilisateur actif sur lecoffre.io"],
	"folder.participant.accessInfo.field.idNotOffice": ["folder", "Identifiant IdNot de l’étude du membre"],
	"folder.participant.accessInfo.field.fullName": ["folder", "Prénom et nom"],
	"folder.participant.accessInfo.field.shareRole": ["folder", "Rôle dans l’étude du membre"],
	"folder.participant.accessInfo.field.officeName": ["folder", "Nom de l’étude du membre"],
	"folder.participant.accessInfo.field.officeSubscription": ["folder", "Type d’abonnement lecoffre.io de l’étude du membre"],
	"folder.participant.accessInfo.field.crpcen": ["folder", "CRPCEN de l’étude du membre"],
	"folder.participant.accessInfo.field.email": ["folder", "E-mail"],
	"folder.participant.accessInfo.field.phone": ["folder", "Téléphone"],
	"folder.participant.accessInfo.field.loginLink": ["folder", "Lien de connexion (dossier et étude)"],
	"folder.participant.accessInfo.field.smsCode": ["folder", "Code d’accès (SMS)"],
	"folder.participant.accessInfo.field.otpEmailCode": ["folder", "Code d’accès (e-mail)"],
	"folder.participant.accessInfo.value.yes": ["folder", "Oui"],
	"folder.participant.accessInfo.value.no": ["folder", "Non"],
	"folder.participant.accessInfo.value.missing": ["folder", "—"],
	"folder.participant.shareRole.OWNER": ["folder", "Propriétaire du partage"],
	"folder.participant.shareRole.CONTRIBUTOR": ["folder", "Contributeur"],
	"folder.participant.shareRole.GUEST_NOTARY": ["folder", "Notaire invité"],
	"folder.participant.shareRole.VIEWER": ["folder", "Lecture seule"],
	"folder.participant.subscriptionType.STANDARD": ["folder", "Standard"],
	"folder.participant.subscriptionType.UNLIMITED": ["folder", "Illimité"],
	"folder.participant.subscriptionType.NOTARIAT": ["folder", "Notariat"],
	"folder.participant.subscriptionType.TRIAL": ["folder", "Essai"],
	"folder.participant.subscriptionType.BASIC": ["folder", "Basique"],
	"folder.participant.subscriptionType.PREMIUM": ["folder", "Premium"],
	"folder.participant.subscriptionType.ENTERPRISE": ["folder", "Entreprise"],
	"forms.client.noteLabel": ["forms", "Note client (facultatif)"],
	"forms.removeMemberConfirmTitle": ["forms", "Retirer le membre ?"],
	"forms.sharedNotary.invitedNotaryEmailLabel": ["forms", "Email du notaire invité"],
	"forms.sharedNotary.invitedNotaryEmailPlaceholder": ["forms", "Email du notaire invité"],
	"forms.sharedNotary.noteLabel": ["forms", "Note notaire invité (facultatif)"],
	"forms.sharedNotary.notePlaceholder": ["forms", "Note à l'attention du notaire invité"],
	"forms.thirdParty.noteLabel": ["forms", "Note tiers (facultatif)"],
	"forms.thirdParty.notePlaceholder": ["forms", "Note à l'attention du tiers"],
	"forms.thirdParty.roleDescriptionLabel": ["forms", "Description du rôle"],
	"forms.thirdParty.roleDescriptionPlaceholder": ["forms", "Description du rôle"],
	"login.errorTitle": ["login", "Erreur de connexion"],
	"login.error.http401.title": ["login", "Accès refusé"],
	"login.error.http401.body": [
		"login",
		"Vous n'avez pas les droits nécessaires pour effectuer cette action. Vérifiez vos permissions ou contactez votre administrateur.",
	],
	"login.modal.missingSubscription.title": ["login", "Abonnement requis"],
	"login.modal.missingSubscription.body": [
		"login",
		"Pour accéder à cette fonctionnalité, votre structure doit disposer d’un abonnement actif.",
	],
	"login.modal.missingSubscription.confirm": ["login", "Voir l’abonnement"],
	"login.modal.missingProfessionalEmail.title": ["login", "E-mail professionnel requis"],
	"login.modal.missingProfessionalEmail.body": ["login", loginMissingProfessionalEmailBodyFallback()],
	"login.modal.missingProfessionalEmail.confirm": ["login", "Compris"],
	"login.modal.idnotUnavailable.title": ["login", "Connexion Id.Not indisponible"],
	"login.modal.idnotUnavailable.body": [
		"login",
		"Le service d’authentification Id.Not est momentanément indisponible ou la configuration est incomplète. Réessayez plus tard ou contactez le support.",
	],
	"login.modal.generic.cancel": ["login", "Annuler"],
	"login.form.email.label": ["login", "E-mail"],
	"login.form.email.placeholder": ["login", "Adresse e-mail"],
	"login.form.newPassword.placeholder": ["login", "Nouveau mot de passe"],
	"login.form.password.confirmPlaceholder": ["login", "Confirmer le mot de passe"],
	"login.form.password.hint": [
		"login",
		"8 caractères minimum, dont une majuscule, une minuscule, un chiffre et un caractère spécial.",
	],
	"login.form.password.placeholder": ["login", "Mot de passe"],
	"login.form.password.title": ["login", "Connexion"],
	"login.form.passwordForgotten.link": ["login", "Mot de passe oublié ?"],
	"login.form.submit": ["login", "Continuer"],
	"login.form.submit.validate": ["login", "Valider"],
	"login.modal.passwordForgotten.body": [
		"login",
		"Un e-mail de réinitialisation va vous être envoyé si l'adresse est reconnue.",
	],
	"login.modal.passwordForgotten.cancel": ["login", "Annuler"],
	"login.modal.passwordForgotten.title": ["login", "Mot de passe oublié"],
	"login.modal.sessionExpired.title": ["login", "Session expirée"],
	"login.modal.sessionExpired.body": [
		"login",
		"Votre session a expiré ou a été révoquée. Veuillez vous reconnecter.",
	],
	"login.modal.sessionExpired.confirm": ["login", "Se reconnecter"],
	"login.header.subtitle": ["login", "Connectez-vous pour accéder à votre espace sécurisé."],
	"login.header.title": ["login", "Bienvenue !"],
	"login.section.clients.title": ["login", "Accès Clients"],
	"login.section.notaries.cta": ["login", "S'identifier avec ID.not"],
	"login.section.notaries.subscribeCta": ["login", "S'abonner avec Idnot"],
	"login.section.notaries.title": ["login", "Accès Notaires et les Collaborateurs"],
	"login.stepNewPassword.title": ["login", "Nouveau mot de passe"],
	"login.stepPasswordForgotten.title": ["login", "Réinitialisation du mot de passe"],
	"login.stepTotp.email.title": ["login", "Code envoyé à"],
	"login.stepTotp.form.placeholder": ["login", "Code de vérification"],
	"login.stepTotp.form.submit": ["login", "Valider"],
	"login.stepTotp.resend.cta": ["login", "Renvoyer un code"],
	"login.stepTotp.resend.question": ["login", "Vous n'avez pas reçu le code ?"],
	"login.stepTotp.resend.timer": ["login", "Prochain envoi dans"],
	"login.stepTotp.title.prefix": ["login", "Code envoyé au"],
	"myAccount.contact.contactEmail.label": ["my-account", "E-mail contact LeCoffre"],
	"myAccount.contact.contactEmail.placeholder": ["my-account", "E-mail contact LeCoffre"],
	"myAccount.contact.idnotEmail.label": ["my-account", "E-mail IdNot (annuaire)"],
	"myAccount.contact.idnotEmail.placeholder": ["my-account", "E-mail IdNot (annuaire)"],
	"myAccount.contact.shareInviteEmail.label": ["my-account", "E-mail invitation partage"],
	"myAccount.contact.shareInviteEmail.placeholder": ["my-account", "E-mail invitation partage"],
	"myAccount.contact.firstName.label": ["my-account", "Prénom"],
	"myAccount.contact.firstName.placeholder": ["my-account", "Prénom"],
	"myAccount.contact.lastName.label": ["my-account", "Nom"],
	"myAccount.contact.lastName.placeholder": ["my-account", "Nom"],
	"myAccount.contact.phone.label": ["my-account", "Numéro de téléphone"],
	"myAccount.contact.phone.placeholder": ["my-account", "Numéro de téléphone"],
	"myAccount.contact.seatCount.label": ["my-account", "Nombre de sièges"],
	"myAccount.contact.seatCount.placeholder": ["my-account", "Nombre de sièges"],
	"myAccount.header.subtitle": [
		"my-account",
		"Gérez vos informations personnelles et celles de votre étude.",
	],
	"myAccount.header.title": ["my-account", "Mon compte"],
	"myAccount.idnotPortal.button": ["my-account", "Accès à ID.not"],
	"myAccount.resetSession.button": ["my-account", "Effacer les cookies et me déconnecter"],
	"myAccount.resetSession.error": [
		"my-account",
		"Impossible de réinitialiser la session. Fermez l'onglet ou contactez le support.",
	],
	"myAccount.office.address.label": ["my-account", "Adresse"],
	"myAccount.office.address.placeholder": ["my-account", "Adresse"],
	"myAccount.office.addressFallback": ["my-account", "Adresse non renseignée"],
	"myAccount.office.city.label": ["my-account", "Ville"],
	"myAccount.office.city.placeholder": ["my-account", "Ville"],
	"myAccount.office.crpcen.label": ["my-account", "CRPCEN"],
	"myAccount.office.crpcen.placeholder": ["my-account", "CRPCEN"],
	"myAccount.office.idNot.label": ["my-account", "Identifiant ID.not"],
	"myAccount.office.idNot.placeholder": ["my-account", "Identifiant ID.not"],
	"myAccount.office.role.label": ["my-account", "Rôle dans l'étude"],
	"myAccount.office.role.placeholder": ["my-account", "Rôle dans l'étude"],
	"myAccount.office.role.fallback": ["my-account", "Non renseigné"],
	"myAccount.office.licenseActive": ["my-account", "Abonnement actif"],
	"myAccount.office.licenseInactive": ["my-account", "Abonnement inactif"],
	"myAccount.office.name.label": ["my-account", "Nom de l'étude"],
	"myAccount.office.name.placeholder": ["my-account", "Nom de l'étude"],
	"myAccount.office.nameFallback": ["my-account", "Étude"],
	"myAccount.office.sectionTitle": ["my-account", "Étude notariale"],
	"myAccount.office.subscription.endDate.label": ["my-account", "Fin de période"],
	"myAccount.office.subscription.loading": ["my-account", "Chargement de l'abonnement..."],
	"myAccount.office.subscription.none": ["my-account", "Aucun abonnement actif sur l'étude"],
	"myAccount.office.subscription.sectionTitle": ["my-account", "Abonnement de l'étude"],
	"myAccount.office.subscription.seat.active": ["my-account", "Siège actif"],
	"myAccount.office.subscription.seat.inactive": ["my-account", "Pas de siège"],
	"myAccount.office.subscription.seat.label": ["my-account", "Mon siège"],
	"myAccount.office.subscription.status.active": ["my-account", "Actif"],
	"myAccount.office.subscription.status.inactive": ["my-account", "Inactif"],
	"myAccount.office.subscription.status.label": ["my-account", "Statut"],
	"myAccount.office.subscription.type.label": ["my-account", "Forfait"],
	"myAccount.office.subscription.type.standard": ["my-account", "Standard"],
	"myAccount.office.subscription.type.unknown": ["my-account", "Non renseigné"],
	"myAccount.office.subscription.type.unlimited": ["my-account", "Illimité"],
	"myAccount.page.title": ["my-account", "Mon compte"],
};

export const SITE_TEXT_FALLBACKS_FR: SiteTextFallbackMap = {
	...SITE_TEXT_FALLBACKS_FR_BASE,
	...SITE_TEXT_FALLBACKS_FR_TAIL,
};

export function getSiteTextFallbackMap(locale: string): SiteTextFallbackMap | null {
	const canonicalLocale = normalizeSiteTextLocale(locale);
	if (canonicalLocale !== "fr-FR") {
		return null;
	}
	return SITE_TEXT_FALLBACKS_FR;
}

export function getSiteTextFallbackDef(textKey: string, locale: string): SiteTextFallbackDef | null {
	const canonicalLocale = normalizeSiteTextLocale(locale);
	const map = getSiteTextFallbackMap(canonicalLocale);
	if (!map) {
		return null;
	}
	const tuple = map[textKey];
	if (!tuple) {
		return null;
	}
	const [scope, content] = tuple;
	return { textKey, scope, content: applyFolderAsOperationFr(content) };
}

/** Contenu seul (ex. repli quand le CMS renvoie une chaîne vide ou blanche). */
export function getSiteTextFallbackContent(textKey: string, locale: string): string | null {
	const def = getSiteTextFallbackDef(textKey, locale);
	return def ? def.content : null;
}
